/* eslint-disable max-lines */
import { Component, inject } from '@angular/core';
import { FormControl, FormGroup, NonNullableFormBuilder, UntypedFormControl, Validators } from '@angular/forms';
import { createMask } from '@ngneat/input-mask';
import { LatLngTuple } from 'leaflet';
import { HomeFeedItem } from '@bn/api-interfaces';
import { AdvancedSettings } from '@bn/dashboard-pages/magic-containers/components/advanced-settings/models';
import { EnvironmentVariablesConfig } from '@bn/dashboard-shared/components';
import { UserFeed } from '@bn/dashboard-shared/models';
import { AbstractLazyDirective } from '@bn/shared/entities/abstracts/abstract-lazy.directive';
import { NotificationType, NotificationTypeNames } from '@bn/shared/entities/enums';
import { StringNumberMap } from '@bn/shared/entities/models';
import { ContentService } from '@bn/shared/services';
import { ItemStyle } from '@bn/web-ui/button/models';
import { DropdownMenuItem } from '@bn/web-ui/dropdown-menu';
import { EntityOverviewItem } from '@bn/web-ui/entity-overview';
import { InputAction } from '@bn/web-ui/input/enums';
import { InputOptions } from '@bn/web-ui/input/models';
import { FormListItem } from '@bn/web-ui/models';
import { PricingItemType, ReservedType, SharedType } from '@bn/web-ui/pricing-card/models';
import { InputListModel } from '@bn/web-ui/select-input-list/models';
import { TableLinkClick, TableRow } from '@bn/web-ui/table';
import { TabItem } from '@bn/web-ui/tabs/models';

@Component({
  templateUrl: './demo-components.page.html',
  styleUrls: ['./demo-components.page.scss'],
  standalone: false,
})
export class DemoComponentsPage extends AbstractLazyDirective {
  #contentService = inject(ContentService);
  badgeColors = ['default', 'base', 'blue', 'red', 'yellow', 'green', 'premium'];

  miniInputForm = new FormGroup({
    data: new FormControl<string>(''),
  });

  public inputMask = createMask({
    regex: String.raw`(?:\d+:(?:[0-5]\d):(?:[0-5]\d)`,
    parser: (value) => {
      const [hours, minutes, seconds] = value.split(':').map((n: string) => +n.replace('_', '') || 0);
      return +hours * 60 * 60 + (+minutes || 0) * 60 + (+seconds || 0);
    },
  });
  public advancedSettingsForm = new FormGroup({
    advancedSettings: new FormControl<AdvancedSettings>({
      alwaysPullImage: true,
      startupCommand: '',
      startupCommands: ['ls', '-la'],
      containerArgs: '',
      workingDir: '',
    }),
  });
  public startupStatusEnabled = false;
  public geoCoords: LatLngTuple = [46.028912, 14.644783];
  public tabItems: TabItem[] = [
    {
      name: 'Origin URL',
      tooltip: 'Testing tooltip',
      contextType: 'danger',
      contextIcon: 'triangle-exclamation',
    },
    {
      name: 'Storage',
      title: 'Testing title',
    },
    {
      name: 'DNS Record',
    },
  ];
  public activeTab = 0;
  public radioTabItems: FormListItem[] = [
    {
      label: 'Origin URL',
      name: 'origin-url',
      value: 'origin-url',
    },
    {
      label: 'Storage',
      name: 'storage',
      value: 'storage',
    },
    {
      label: 'DNS Record',
      name: 'dns-record',
      value: 'dns-record',
    },
  ];
  public dropdownItems: DropdownMenuItem[] = [
    {
      type: 'link',
      label: 'Account settings',
      route: '/account/settings',
      icon: 'circle-user',
    },
    { type: 'link', label: 'GDPR', route: '/account/dpa', icon: 'envelope-open' },
    {
      type: 'link',
      label: 'Change password',
      route: '/account/settings/changepassword',
      icon: 'key',
    },
    { type: 'divider' },
    {
      type: 'link',
      label: 'Log out',
      action: 'logout',
      icon: 'right-from-bracket',
      class: 'bn-dropdown__item--light',
    },
    { type: 'divider' },
    {
      type: 'link',
      label: 'Success',
      action: 'Success',
      icon: 'circle-check',
      contextType: 'success',
    },
    {
      type: 'link',
      label: 'Info',
      action: 'Info',
      icon: 'circle-exclamation',
      contextType: 'info',
    },
    {
      type: 'link',
      label: 'Delete',
      action: 'delete',
      icon: 'trash-can',
    },
  ];
  public inputsList: InputListModel = {
    rows: [
      { input: '100px', select: 0 },
      { input: '100%', select: 1 },
      { input: '10px', select: 1 },
    ],
  };
  public inputButtonStyle: ItemStyle = 'link';
  public inputWithSubmitOptions: InputOptions = {
    rightAddons: [
      {
        value: 'Save',
        type: 'button',
        itemStyle: this.inputButtonStyle,
        action: InputAction.ButtonClick,
      },
    ],
  };
  public inputWithSubmitModel!: string;
  public shortListTableItems: TableRow[] = [
    {
      itemId: 'row 1 id',
      columns: [
        {
          value: 'no-matter-what-happens',
          valueType: 'link',
          leftIcon: { icon: 'location-dot' },
          action: 'view',
          id: '',
        },
        {
          valueType: 'string',
          id: '',
          value: 'mojadomena.si',
        },
        {
          value: 'Standard',
          valueType: 'badge',
          id: '',
          valueTypeOptions: {
            badgeType: 'default',
            badgeSize: 'sm',
            contextType: 'success',
          },
        },
      ],
    },
    {
      itemId: 'row 2 id',
      columns: [
        {
          value: 'no-matter-what-happens',
          valueType: 'link',
          leftIcon: { icon: 'location-dot' },
          action: 'view',
          id: '',
        },
        {
          valueType: 'string',
          id: '',
          value: 'mojadomena.si',
        },
        {
          value: 'Standard',
          valueType: 'badge',
          id: '',
          valueTypeOptions: {
            badgeType: 'default',
            badgeSize: 'sm',
            contextType: 'success',
          },
        },
      ],
    },
    {
      itemId: 'row 3 id',
      columns: [
        {
          value: 'no-matter-what-happens',
          valueType: 'link',
          leftIcon: { icon: 'location-dot' },
          action: 'view',
          id: '',
        },
        {
          valueType: 'string',
          id: '',
          value: 'mojadomena.si',
        },
        {
          id: '',
          value: '',
        },
      ],
    },
  ];
  public shortListPostItems: HomeFeedItem[] = [
    {
      ImageUrl: 'https://picsum.photos/650',
      Url: 'https://www.bunny.net/',
      Title: 'Title here for the guide',
      Summary:
        'Invite your coworkers to bunny and teach them something really amazing. Invite your coworkers to bunny and teach them something really amazing. Invite your coworkers to bunny and teach them something really amazing. Invite your coworkers to bunny and teach them something really amazing.',
    },
    {
      ImageUrl: 'https://picsum.photos/65?a',
      Url: 'https://www.bunny.net/',
      Title: 'Bunny.net onboarding',
      Summary: 'Invite your coworkers to bunny and teach them something really amazing.',
    },
    {
      ImageUrl: 'https://picsum.photos/65?b',
      Url: 'https://www.bunny.net/',
      Title: 'Title here for the guide',
      Summary: 'Invite your coworkers to bunny and teach them something really amazing.',
    },
    {
      ImageUrl: 'https://picsum.photos/65?c',
      Url: 'https://www.bunny.net/',
      Title: 'Bunny.net onboarding',
      Summary: 'Invite your coworkers to bunny and teach them something really amazing.',
    },
  ];
  public formGroupWidgetConfig1: EnvironmentVariablesConfig = {
    head: {
      id: { name: 'Key' },
      text_delimiter: { name: '' },
      default_value: { name: 'Default value' },
      required: { name: 'Required' },
      is_on: { name: 'On/Off' },
    },
    addButton: {
      action: 'add',
      icon: 'plus',
      label: 'Add new env variable',
      placement: 'top',
    },
    addRow: {
      items: {
        id: { type: 'input', placeholder: 'Key', size: 'sm' }, // there must be an id field (doubles as row key), else it will be added an generic one
        text_delimiter: { type: 'text', value: '<-Text->' },
        default_value: {
          type: 'input',
          placeholder: 'Default Value',
        },
        required: { type: 'checkbox', size: 'md' },
        is_on: { type: 'toggle', size: 'md' },
      },
    },

    editRow: {
      items: {
        id: { type: 'input', readonly: true },
        text_delimiter: { type: 'text', value: '<-Text->' },
        default_value: {
          type: 'input',
          placeholder: 'Not Set',
        },
        spacer: { type: 'spacer' },
        is_on: { type: 'toggle', size: 'md' },
      },
    },
  };
  public formGroupWidgetConfig2: EnvironmentVariablesConfig = {
    head: {
      id: { name: 'Key' },
      default_value: { name: 'Default value' },
      required: { name: 'Required' },
      is_on: { name: 'On/Off' },
    },
    addButton: {
      action: 'append',
      icon: 'plus',
      itemStyle: 'link',
      label: 'Add new env variable',
      placement: 'bottom',
    },
    addRow: {
      items: {
        id: { type: 'input', placeholder: 'Key', size: 'sm' },
        default_value: {
          type: 'input',
          placeholder: 'Default Value',
        },
        required: { type: 'checkbox', size: 'md' },
        is_on: { type: 'toggle', size: 'md' },
        add_button: {
          type: 'button',
          icon: 'plus',
          size: 'sm',
          label: 'Add',
        },
      },
    },
    editRow: {
      isRemovable: false,
      items: {
        id: { type: 'input', readonly: true, value: 'non_removeable' },
        default_value: {
          type: 'input',
          placeholder: 'Not Set',
        },
        required: { type: 'checkbox', size: 'md', value: true },
        is_on: { type: 'toggle', size: 'md' },
      },
    },
  };
  public formWidgetText1 = 'Type to make a change...';
  public formWidgetText2 = 'Type to make a change...';
  public notification: UserFeed = {
    contextType: 'danger',
    description: 'Simple long description that never ends, like time before paycheck',
    feedType: 'user-notification',
    id: 123,
    isRead: false,
    secondsAgo: 300,
    date: '2022-11-28T00:00:00',
    timestamp: '2022-11-28T13:10:00',
    title: 'Simple one #1',
    type: NotificationType.Problem,
    typeName: NotificationTypeNames.Problem,
  };
  public notification2: UserFeed = {
    ...this.notification,
    contextType: 'info',
    title: 'Simple one #2',
    type: NotificationType.Info,
    typeName: NotificationTypeNames.Info,
  };
  public notification3: UserFeed = {
    ...this.notification,
    contextType: 'warning',
    title: 'Simple one #3 with read more link',
    type: NotificationType.Warning,
    typeName: NotificationTypeNames.Warning,
    url: 'https://bunny.net/docs',
  };
  public datePickerValue = '2022-12-13T00:00:00Z';
  public dateRangePickerValue = ['2022-10-13T00:00:00Z', '2022-10-23T00:00:00Z'];
  public datePickerMaxDate = '2022-12-24T00:00:00Z';
  public datePickerMinDate = '2022-12-1T00:00:00Z';
  public datePickerDatesDisabled = ['2022-12-14T00:00:00Z', '2022-12-15T00:00:00Z'];
  public fixedPriceItem: PricingItemType<SharedType> = {
    id: '0',
    type: 'shared',
    title: 'Pricing',
    cpu: '0.02688',
    disc: '0.1',
    memory: '0.2',
  };
  public listPriceItem1: PricingItemType<ReservedType> = {
    id: '1',
    type: 'reserved',
    title: '1',
    description: '3',
    memory: '8',
    storage: '1',
  };
  public listPriceItem2: PricingItemType<ReservedType> = {
    id: '2',
    type: 'reserved',
    title: '2',
    description: '5',
    memory: '8',
    storage: '1',
  };
  public listPriceItem3: PricingItemType<ReservedType> = {
    id: '3',
    type: 'reserved',
    title: '3',
    description: '8',
    memory: '8',
    storage: '1',
  };
  public listPriceItem4: PricingItemType<ReservedType> = {
    id: '4',
    type: 'reserved',
    title: '5',
    description: '11',
    memory: '8',
    storage: '1',
  };
  public listPriceItem5: PricingItemType<ReservedType> = {
    id: '5',
    type: 'reserved',
    title: '8',
    description: '15',
    memory: '8',
    storage: '1',
  };
  public listPriceItem6: PricingItemType<ReservedType> = {
    id: '6',
    type: 'reserved',
    title: '12',
    description: '55',
    memory: '8',
    storage: '1',
  };
  public selectedStepsIndex = 1;
  public linksExplore = [
    { text: 'Overview', url: 'https://bunny.net/' },
    { text: 'Developer guide', url: 'https://bunny.net/' },
    { text: 'Tutorial', url: 'https://bunny.net/' },
  ];
  public linksGuides = [
    { text: 'All about security', url: 'https://bunny.net/' },
    { text: 'How to setup IPs', url: 'https://bunny.net/' },
    { text: 'What is S3 Authentication', url: 'https://bunny.net/' },
  ];
  public containerOverviewItems: EntityOverviewItem[] = [
    {
      icon: 'chart-tree-map',
      title: 'Registry',
      description: 'Dockerhub public',
    },
    {
      icon: 'chart-tree-map',
      title: 'Image',
      description: 'public/image-name',
    },
    {
      icon: 'square-bolt',
      title: 'Monitoring',
      description: 'Startup, Liveness',
    },
    {
      icon: 'server',
      title: 'Package',
      description: 'Shared',
    },
  ];
  public chartData: StringNumberMap = {
    '2023-08-01T00:00:00Z': 11065583586,
    '2023-08-02T00:00:00Z': 11086882573,
    '2023-08-03T00:00:00Z': 11104931890,
    '2023-08-04T00:00:00Z': 11122057552,
    '2023-08-05T00:00:00Z': 11138511722,
    '2023-08-06T00:00:00Z': 11152127452,
    '2023-08-07T00:00:00Z': 11166143011,
    '2023-08-08T00:00:00Z': 11182455564,
    '2023-08-09T00:00:00Z': 11200156612,
    '2023-08-10T00:00:00Z': 11215833497,
    '2023-08-11T00:00:00Z': 11229162251,
    '2023-08-12T00:00:00Z': 11244756080,
    '2023-08-13T00:00:00Z': 11257498491,
    '2023-08-14T00:00:00Z': 11270831016,
    '2023-08-15T00:00:00Z': 11288187249,
    '2023-08-16T00:00:00Z': 11304197580,
    '2023-08-17T00:00:00Z': 11320315186,
    '2023-08-18T00:00:00Z': 11335477993,
    '2023-08-19T00:00:00Z': 11350492902,
    '2023-08-20T00:00:00Z': 11363013421,
    '2023-08-21T00:00:00Z': 11377153120,
    '2023-08-22T00:00:00Z': 11392744212,
    '2023-08-23T00:00:00Z': 11407905138,
    '2023-08-24T00:00:00Z': 11425892093,
    '2023-08-25T00:00:00Z': 11442328168,
    '2023-08-26T00:00:00Z': 11458870544,
    '2023-08-27T00:00:00Z': 11473313697,
    '2023-08-28T00:00:00Z': 11487125479,
    '2023-08-29T00:00:00Z': 11503548077,
    '2023-08-30T00:00:00Z': 11517642562,
    '2023-08-31T00:00:00Z': 11534019158,
  };
  private readonly fb = inject(NonNullableFormBuilder);
  public inputsForm = this.fb.group({
    inputXS: new UntypedFormControl(),
    inputSM: new UntypedFormControl(),
    inputMD: new UntypedFormControl(),
    inputLG: new UntypedFormControl(),
    selectXXS: new UntypedFormControl(),
    selectXS: new UntypedFormControl(),
    selectSM: new UntypedFormControl(),
    selectMD: new UntypedFormControl(),
    selectLG: new UntypedFormControl(),
  });
  public numberForm = this.fb.group({
    numberInput: this.fb.control(3, [Validators.min(2), Validators.max(5)]),
  });
  public inputMaskForm = this.fb.group({
    timeInput: this.fb.control(''),
  });
  public listPackagesForm = this.fb.group({
    cardItem: this.fb.control(''),
  });
  public inputOverrideForm = this.fb.group({
    overrideInput: this.fb.control(''),
  });
  public form = this.fb.group({ test: ['origin-url'] });
  public inputsListForm = this.fb.group({
    inputsList: new UntypedFormControl({
      rows: [
        { input: '100px', select: 0 },
        { input: '100%', select: 1 },
        { input: '10px', select: 1 },
      ],
    }),
  });
  public formGroupWidgetForm1 = this.fb.group({
    groupWidgetForm1: new UntypedFormControl(),
  });
  public formGroupWidgetForm2 = this.fb.group({
    groupWidgetForm2: new UntypedFormControl(),
  });
  public selectInputFormList = this.fb.group({
    list: this.fb.array([
      this.fb.group({
        input: this.fb.control('Some Value', [Validators.required]),
        select: this.fb.control('SelectedType', [Validators.required]),
      }),
    ]),
  });

  public levelIndex = 2;

  constructor() {
    super();

    this.#contentService.setContainerSize('auto');

    this.subscriptions.add(
      this.form.valueChanges.subscribe((value) => {
        this.toastr.info('Form value changed: ', JSON.stringify(value, null, '\t'));
      }),
    );

    this.toastr.info('Form Value: ', JSON.stringify(this.form.value));
    this.subscriptions.add(this.formGroupWidgetForm1.valueChanges.subscribe((formValues) => (this.formWidgetText1 = JSON.stringify(formValues, null, '\t'))));
    this.subscriptions.add(this.formGroupWidgetForm2.valueChanges.subscribe((formValues) => (this.formWidgetText2 = JSON.stringify(formValues, null, '\t'))));
  }

  public onLevelChange(levelIndex: number) {
    alert(levelIndex);
  }

  public onActiveTabChange(tabIndex: number) {
    this.toastr.info('Active tab changed: ' + tabIndex);
    this.activeTab = tabIndex;
  }

  public inputWithSubmitClick() {
    this.toastr.info('Button clicked: ', `Input value: ${this.inputWithSubmitModel}`);
  }

  public onInputButtonStyleChange() {
    this.inputButtonStyle = this.inputButtonStyle === 'primary' ? 'secondary' : 'primary';

    this.inputWithSubmitOptions = {
      rightAddons: [
        {
          value: 'Save',
          type: 'button',
          itemStyle: this.inputButtonStyle,
          action: InputAction.ButtonClick,
        },
      ],
    };
  }

  public onShortListLinkClicked(event: TableLinkClick) {
    this.toastr.info('Link clicked', JSON.stringify(event));
  }

  public onShortListHeaderButtonClicked() {
    this.toastr.info('Show all button clicked.', 'Do whatever you want to do with the event.');
  }

  public onStepClick(stepIndex: number) {
    this.selectedStepsIndex = stepIndex;
  }

  actionableItemClick() {
    this.miniInputForm.get('data')?.setValue('value is now changed!');
  }
}
